import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    ProductCategoryInput,
} from '@barboxx/admin-components';
import {
    CREATE_ALCOHOL_TYPE_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductCategory, ProductType } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

const columns: GridColDef[] = [
    { field: 'id', headerName: 'Id', width: ID_WIDTH },
    { field: 'name', headerName: 'Name', minWidth: NAME_MIN_WIDTH },
    {
        field: 'description',
        headerName: 'Description',
        minWidth: 170,
        flex: 1,
    },
    {
        field: 'abvRange',
        headerName: 'ABV Range',
        width: 130,
        maxWidth: 130,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'isAlcoholic',
        headerName: 'Is Alcoholic',
        width: 200,
        type: 'boolean',
    },
];

interface Props {
    productCategory: ProductCategory;
    refetch: () => void;
}

export const ProductTypesCard = ({ productCategory, refetch }: Props) => {
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<ProductType.CreateRequest>({
        resolver: yupResolver(CREATE_ALCOHOL_TYPE_SCHEMA),
        defaultValues: {
            productCategoryId: productCategory.id,
        },
    });

    const mutateCreateProductType = useMutation({
        mutationKey: ['createProductType'],
        mutationFn: (data: ProductType.CreateRequest) =>
            Api.Products.Types.postProductType(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: ProductType.CreateRequest) => {
        return mutateCreateProductType
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${data?.name} type!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating product type!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams) => {
        navigate(`/products/types/${params.id}`);
    };

    return (
        <>
            <DataGridCard
                title="Alcohol Types List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Type
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={productCategory.types}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Alcohol Type
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <TextFieldElement label="Name" name="name" fullWidth />
                        <TextFieldElement
                            label="Description"
                            name="description"
                            fullWidth
                        />
                        <TextFieldElement
                            label="ABV Range"
                            name="abvRange"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Production Method"
                            name="productionMethod"
                            fullWidth
                        />
                        <ProductCategoryInput disabled />
                        <CheckboxElement
                            name="isAlcoholic"
                            label="Is Alcoholic"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
